import {
  Component,
  OnInit,
  OnDestroy,
  inject,
  ViewChild,
  ElementRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  WidgetService,
  Widget,
  WidgetTemplate,
} from '../../../core/widget.service';
import { PWAService } from '../../../core/pwa.service';
import { Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'app-dashboard-widgets',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="dashboard-container p-4 lg:p-6">
      <!-- Header -->
      <div
        class="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-4"
      >
        <div>
          <h1 class="text-2xl font-bold text-gray-900">
            Dashboard Personalizado
          </h1>
          <p class="text-gray-600 mt-1">
            Widgets personalizáveis para sua gestão
          </p>
        </div>

        <div class="flex items-center gap-3">
          <!-- Edit Mode Toggle -->
          <button
            (click)="toggleEditMode()"
            class="flex items-center gap-2 px-4 py-2 rounded-lg transition-colors"
            [class.bg-blue-600]="isEditMode"
            [class.text-white]="isEditMode"
            [class.bg-gray-100]="!isEditMode"
            [class.text-gray-700]="!isEditMode"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <path
                d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"
              />
              <path
                d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"
              />
            </svg>
            {{ isEditMode ? 'Concluir' : 'Personalizar' }}
          </button>

          <!-- Add Widget Button -->
          <button
            *ngIf="isEditMode"
            (click)="showAddWidget = true"
            class="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            Adicionar Widget
          </button>

          <!-- More Options -->
          <div class="relative">
            <button
              (click)="showOptionsMenu = !showOptionsMenu"
              class="p-2 text-gray-600 hover:text-gray-800 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <circle cx="12" cy="12" r="1" />
                <circle cx="19" cy="12" r="1" />
                <circle cx="5" cy="12" r="1" />
              </svg>
            </button>

            <!-- Options Dropdown -->
            <div
              *ngIf="showOptionsMenu"
              class="absolute right-0 top-full mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-50"
            >
              <button
                (click)="refreshAllWidgets()"
                class="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-t-lg"
              >
                🔄 Atualizar Todos
              </button>
              <button
                (click)="exportLayout()"
                class="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
              >
                📤 Exportar Layout
              </button>
              <button
                (click)="importLayout()"
                class="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
              >
                📥 Importar Layout
              </button>
              <button
                (click)="resetToDefault()"
                class="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 rounded-b-lg"
              >
                🔄 Resetar Padrão
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Widget Grid -->
      <div class="widget-grid" [class.edit-mode]="isEditMode">
        <div
          *ngFor="let widget of visibleWidgets; trackBy: trackByWidgetId"
          class="widget-container"
          [attr.data-widget-id]="widget.id"
          [class]="getWidgetClasses(widget)"
          [style.background-color]="
            widget.config.transparentBackground ? 'transparent' : 'white'
          "
          [style.border]="
            widget.config.showBorder ? '1px solid #e5e7eb' : 'none'
          "
          [style.box-shadow]="
            widget.config.showShadow ? '0 1px 3px rgba(0,0,0,0.1)' : 'none'
          "
          [style.border-radius]="widget.config.roundedCorners ? '0.75rem' : '0'"
        >
          <!-- Widget Header -->
          <div
            *ngIf="widget.config.showTitle || isEditMode"
            class="widget-header flex items-center justify-between p-4 pb-2"
          >
            <div class="flex items-center gap-2">
              <div
                *ngIf="widget.config.showIcon && widget.icon"
                class="widget-icon w-6 h-6 rounded flex items-center justify-center"
                [style.background-color]="widget.color + '20'"
                [style.color]="widget.color"
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <!-- Dynamic icon based on widget.icon -->
                  <path
                    *ngIf="widget.icon === 'trending_up'"
                    d="m3 17 6-6 4 4 8-8"
                  />
                  <path
                    *ngIf="widget.icon === 'warning'"
                    d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"
                  />
                  <circle
                    *ngIf="widget.icon === 'people'"
                    cx="12"
                    cy="12"
                    r="10"
                  />
                </svg>
              </div>
              <div>
                <h3
                  *ngIf="widget.config.showTitle"
                  class="font-semibold text-gray-900"
                >
                  {{ widget.title }}
                </h3>
                <p
                  *ngIf="widget.config.showSubtitle && widget.subtitle"
                  class="text-sm text-gray-600"
                >
                  {{ widget.subtitle }}
                </p>
              </div>
            </div>

            <!-- Widget Controls (Edit Mode) -->
            <div *ngIf="isEditMode" class="flex items-center gap-1">
              <button
                (click)="refreshWidget(widget.id)"
                class="p-1 text-gray-400 hover:text-blue-600 rounded transition-colors"
                title="Atualizar"
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <polyline points="23,4 23,10 17,10" />
                  <path
                    d="M20.49 9A9 9 0 0 0 5.64 5.64L1 10m22 4l-4.64 4.36A9 9 0 0 1 3.51 15"
                  />
                </svg>
              </button>
              <button
                (click)="duplicateWidget(widget.id)"
                class="p-1 text-gray-400 hover:text-green-600 rounded transition-colors"
                title="Duplicar"
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                  <path
                    d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"
                  />
                </svg>
              </button>
              <button
                (click)="deleteWidget(widget.id)"
                class="p-1 text-gray-400 hover:text-red-600 rounded transition-colors"
                title="Excluir"
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <polyline points="3,6 5,6 21,6" />
                  <path
                    d="M19,6v14a2,2,0,0,1-2,2H7a2,2,0,0,1-2-2V6m3,0V4a2,2,0,0,1,2-2h4a2,2,0,0,1,2,2V6"
                  />
                </svg>
              </button>
            </div>
          </div>

          <!-- Widget Content -->
          <div class="widget-content p-4 pt-2">
            <!-- KPI Widget -->
            <div *ngIf="widget.type === 'kpi'" class="text-center">
              <div class="text-3xl font-bold mb-1" [style.color]="widget.color">
                {{ formatCurrency(widget.data.value) }}
              </div>
              <div class="flex items-center justify-center gap-1 text-sm">
                <span
                  class="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium"
                  [class.bg-green-100]="widget.data.changeType === 'increase'"
                  [class.text-green-700]="widget.data.changeType === 'increase'"
                  [class.bg-red-100]="widget.data.changeType === 'decrease'"
                  [class.text-red-700]="widget.data.changeType === 'decrease'"
                >
                  <svg
                    *ngIf="widget.data.changeType === 'increase'"
                    width="12"
                    height="12"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                  >
                    <polyline points="23,6 13.5,15.5 8.5,10.5 1,18" />
                  </svg>
                  <svg
                    *ngIf="widget.data.changeType === 'decrease'"
                    width="12"
                    height="12"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                  >
                    <polyline points="23,18 13.5,8.5 8.5,13.5 1,6" />
                  </svg>
                  {{ widget.data.change }}%
                </span>
              </div>
              <p class="text-xs text-gray-500 mt-1">
                {{ widget.data.comparison }}
              </p>
            </div>

            <!-- Progress Widget -->
            <div *ngIf="widget.type === 'progress'">
              <div class="flex justify-between text-sm mb-2">
                <span class="text-gray-600">Progresso</span>
                <span class="font-semibold">{{ widget.data.percentage }}%</span>
              </div>
              <div class="w-full bg-gray-200 rounded-full h-3 mb-3">
                <div
                  class="h-3 rounded-full transition-all duration-500"
                  [style.width.%]="widget.data.percentage"
                  [style.background-color]="widget.color"
                ></div>
              </div>
              <div class="text-sm text-gray-600">
                <div>
                  {{ formatCurrency(widget.data.current) }} de
                  {{ formatCurrency(widget.data.target) }}
                </div>
                <div class="text-xs">
                  Restam {{ widget.data.daysLeft }} dias
                </div>
              </div>
            </div>

            <!-- Counter Widget -->
            <div *ngIf="widget.type === 'counter'" class="text-center">
              <div class="text-4xl font-bold mb-2" [style.color]="widget.color">
                {{ widget.data.value }}
              </div>
              <div class="text-sm text-gray-600">
                <span
                  class="inline-flex items-center gap-1"
                  [class.text-green-600]="widget.data.growth > 0"
                  [class.text-red-600]="widget.data.growth < 0"
                >
                  {{ widget.data.growth > 0 ? '+' : ''
                  }}{{ widget.data.growth }}% {{ widget.data.period }}
                </span>
              </div>
            </div>

            <!-- Alert Widget -->
            <div *ngIf="widget.type === 'alert'">
              <div class="space-y-2">
                <div
                  *ngFor="let alert of widget.data.alerts.slice(0, 3)"
                  class="flex items-center justify-between p-2 bg-red-50 rounded text-sm"
                >
                  <span class="font-medium text-red-900">{{
                    alert.product
                  }}</span>
                  <span class="text-red-600"
                    >{{ alert.quantity }} unidades</span
                  >
                </div>
                <div
                  *ngIf="widget.data.alerts.length > 3"
                  class="text-xs text-gray-500 text-center"
                >
                  +{{ widget.data.alerts.length - 3 }} mais alertas
                </div>
              </div>
            </div>

            <!-- Quick Action Widget -->
            <div *ngIf="widget.type === 'quickaction'" class="text-center">
              <button
                (click)="executeQuickAction(widget.data)"
                class="w-full py-6 rounded-lg font-semibold text-white hover:opacity-90 transition-opacity"
                [style.background-color]="widget.color"
              >
                {{ widget.data.label }}
              </button>
            </div>

            <!-- Chart Widget (Simplified) -->
            <div *ngIf="widget.type === 'chart'">
              <div class="h-32 flex items-end justify-between gap-1">
                <div
                  *ngFor="let item of widget.data.data"
                  class="flex-1 flex flex-col items-center"
                >
                  <div
                    class="w-full rounded-t transition-all duration-500"
                    [style.height.px]="
                      (item.value / getMaxValue(widget.data.data)) * 100
                    "
                    [style.background-color]="widget.color"
                  ></div>
                  <span class="text-xs text-gray-600 mt-1">{{ item.day }}</span>
                </div>
              </div>
            </div>

            <!-- List Widget -->
            <div *ngIf="widget.type === 'list'">
              <div class="space-y-2">
                <div
                  *ngFor="let item of widget.data.items.slice(0, 4)"
                  class="flex items-center justify-between text-sm"
                >
                  <div>
                    <div class="font-medium text-gray-900">
                      {{ item.title }}
                    </div>
                    <div class="text-xs text-gray-500">{{ item.subtitle }}</div>
                  </div>
                  <span class="font-semibold" [style.color]="widget.color">
                    R$ {{ item.value }}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Add Widget Modal -->
      <div
        *ngIf="showAddWidget"
        class="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
        (click)="showAddWidget = false"
      >
        <div
          class="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[80vh] overflow-hidden"
          (click)="$event.stopPropagation()"
        >
          <div class="p-6 border-b border-gray-200">
            <h2 class="text-xl font-semibold">Adicionar Widget</h2>
          </div>

          <div class="p-6 overflow-y-auto max-h-96">
            <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div
                *ngFor="let template of widgetTemplates"
                class="border border-gray-200 rounded-lg p-4 hover:border-blue-300 cursor-pointer transition-colors"
                (click)="addWidgetFromTemplate(template)"
              >
                <div class="flex items-center gap-3 mb-3">
                  <div
                    class="w-10 h-10 rounded-lg flex items-center justify-center"
                    [style.background-color]="template.defaultColor + '20'"
                    [style.color]="template.defaultColor"
                  >
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                    >
                      <path
                        *ngIf="template.icon === 'trending_up'"
                        d="m3 17 6-6 4 4 8-8"
                      />
                      <path
                        *ngIf="template.icon === 'warning'"
                        d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"
                      />
                    </svg>
                  </div>
                  <div>
                    <h3 class="font-semibold text-gray-900">
                      {{ template.name }}
                    </h3>
                    <p class="text-sm text-gray-600">{{ template.category }}</p>
                  </div>
                </div>
                <p class="text-sm text-gray-600">{{ template.description }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  `,
  styles: [
    `
      .widget-grid {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
        gap: 1.5rem;
        grid-auto-rows: auto;
      }

      .widget-container {
        transition: all 0.3s ease;
        min-height: 200px;
      }

      .widget-container.size-small {
        grid-column: span 1;
        min-height: 150px;
      }

      .widget-container.size-medium {
        grid-column: span 1;
        min-height: 200px;
      }

      .widget-container.size-large {
        grid-column: span 2;
        min-height: 250px;
      }

      .widget-container.size-xlarge {
        grid-column: span 3;
        min-height: 300px;
      }

      .edit-mode .widget-container {
        border: 2px dashed #d1d5db !important;
      }

      .edit-mode .widget-container:hover {
        border-color: #3b82f6 !important;
        transform: scale(1.02);
      }

      @media (max-width: 768px) {
        .widget-grid {
          grid-template-columns: 1fr;
        }

        .widget-container.size-large,
        .widget-container.size-xlarge {
          grid-column: span 1;
        }
      }
    `,
  ],
})
export class DashboardWidgetsComponent implements OnInit, OnDestroy {
  @ViewChild('cameraCapture') cameraCapture!: any;

  private widgetService = inject(WidgetService);
  private pwaService = inject(PWAService);
  private destroy$ = new Subject<void>();

  widgets: Widget[] = [];
  widgetTemplates: WidgetTemplate[] = [];
  isEditMode = false;
  showAddWidget = false;
  showOptionsMenu = false;

  get visibleWidgets(): Widget[] {
    return this.widgets.filter((w) => w.isVisible);
  }

  ngOnInit(): void {
    // Subscribe to widgets
    this.widgetService.widgets$
      .pipe(takeUntil(this.destroy$))
      .subscribe((widgets) => {
        this.widgets = widgets;
      });

    // Subscribe to templates
    this.widgetService.templates$
      .pipe(takeUntil(this.destroy$))
      .subscribe((templates) => {
        this.widgetTemplates = templates;
      });

    // Close modals when clicking outside
    document.addEventListener('click', this.handleDocumentClick.bind(this));
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    document.removeEventListener('click', this.handleDocumentClick.bind(this));
  }

  private handleDocumentClick(event: Event): void {
    const target = event.target as HTMLElement;
    if (!target.closest('.relative')) {
      this.showOptionsMenu = false;
    }
  }

  toggleEditMode(): void {
    this.isEditMode = !this.isEditMode;
    if (!this.isEditMode) {
      this.showAddWidget = false;
    }
  }

  refreshWidget(id: string): void {
    this.widgetService.refreshWidget(id);
  }

  refreshAllWidgets(): void {
    this.widgetService.refreshAllWidgets();
    this.showOptionsMenu = false;
  }

  duplicateWidget(id: string): void {
    this.widgetService.duplicateWidget(id);
  }

  deleteWidget(id: string): void {
    if (confirm('Tem certeza que deseja excluir este widget?')) {
      this.widgetService.deleteWidget(id);
    }
  }

  addWidgetFromTemplate(template: WidgetTemplate): void {
    this.widgetService.addWidget(template);
    this.showAddWidget = false;
  }

  executeQuickAction(data: any): void {
    if (data.action === 'navigate' && data.target) {
      // Router navigation would go here
      console.log('Navigate to:', data.target);
    }
  }

  exportLayout(): void {
    const layout = this.widgetService.exportLayout();
    const blob = new Blob([layout], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'estokai-dashboard-layout.json';
    a.click();
    URL.revokeObjectURL(url);
    this.showOptionsMenu = false;
  }

  importLayout(): void {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json';
    input.onchange = (e: any) => {
      const file = e.target.files[0];
      if (file) {
        const reader = new FileReader();
        reader.onload = (e) => {
          const content = e.target?.result as string;
          if (this.widgetService.importLayout(content)) {
            alert('Layout importado com sucesso!');
          } else {
            alert('Erro ao importar layout. Verifique o arquivo.');
          }
        };
        reader.readAsText(file);
      }
    };
    input.click();
    this.showOptionsMenu = false;
  }

  resetToDefault(): void {
    if (
      confirm(
        'Tem certeza que deseja resetar para o layout padrão? Isso removerá todas as personalizações.'
      )
    ) {
      this.widgetService.resetToDefault();
      this.showOptionsMenu = false;
    }
  }

  getWidgetClasses(widget: Widget): string {
    const classes = ['widget-container'];
    classes.push(`size-${widget.size}`);
    return classes.join(' ');
  }

  formatCurrency(value: number): string {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  }

  getMaxValue(data: any[]): number {
    return Math.max(...data.map((item) => item.value));
  }

  trackByWidgetId(index: number, widget: Widget): string {
    return widget.id;
  }
}
